<script setup lang="ts">
import { computed } from 'vue'
import {
  ECOMMERCE_ITEM_BENEFIT_TYPE,
  type EcommerceItem,
  type EcommerceItemBenefit,
} from '@/types/shared/player-config'
import BaseButton from '@/components/base/BaseButton.vue'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import { formatDuration } from '@/helpers/date'
import { usePack } from '@/composables/game/shop'
import { usePlayer } from '@/composables/game/player'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import { benefitConfigMap, benefitOrder, benefitTypeToKey } from '@/config/benefit-display'

const props = defineProps<{
	limitedPack: EcommerceItem
}>()

const { getStarPrice } = useEcommercePrice()
const { countdownForLimitedPackEnd, remainingPurchaseCount } = usePack(computed(() => props.limitedPack))

const starPrice = computed(() => getStarPrice(props.limitedPack))

const cardStyle = computed(() => ({
  backgroundImage: `url("${props.limitedPack.imageUrl}")`,
  backgroundPosition: 'center',
  backgroundSize: 'cover',
}))

const { goldenCoinValue } = usePlayer()

interface BenefitDisplayItem {
	image: string
	alt: string
	value: string | number
}

const getGoldenCoinBenefitAmount = (benefit: EcommerceItemBenefit) =>
  benefit.amount * goldenCoinValue.value

const formatDurationValue = (amount: number) =>
  amount < 1 ? Math.floor(amount * 60) : Math.floor(amount)

const displayedBenefits = computed(() => {
  const result: Array<{ benefit: EcommerceItemBenefit; type: string }> = []

  for (const benefitType of benefitOrder) {
    if (benefitType === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY) {
      props.limitedPack.benefits
        .filter(b => b.type === benefitType)
        .forEach(benefit => result.push({ benefit, type: 'specialCurrency' }))
    } else {
      const benefit = props.limitedPack.benefits.find(b => b.type === benefitType)
      const type = benefitTypeToKey[benefitType]
      if (benefit && type) {
        result.push({ benefit, type })
      }
    }
  }

  return result
})

const benefitDisplayItems = computed<BenefitDisplayItem[]>(() => {
  const items = displayedBenefits.value.map((item) => {
    if (item.type === 'specialCurrency' && item.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY) {
      return {
        image: allSpecialCurrenciesConfig.value[item.benefit.currency]?.image ?? '',
        alt: allSpecialCurrenciesConfig.value[item.benefit.currency]?.label ?? '',
        value: formatNumberFullTruncate(item.benefit.amount, false, false, true),
      }
    }

    const config = benefitConfigMap[item.type]
    if (!config) return null

    let value: string | number

    if (item.type === 'coin') {
      value = formatNumberFullTruncate(getGoldenCoinBenefitAmount(item.benefit), false, false, true)
    } else if (item.type === 'tphx2' || item.type === 'offlineProfit') {
      value = formatDurationValue(item.benefit.amount)
    } else {
      value = formatNumberFullTruncate(item.benefit.amount, false, false, true)
    }

    return {
      ...config,
      value,
    }
  }).filter((item): item is BenefitDisplayItem => item !== null)

  return items
})
</script>

<template>
  <div
    class="w-full h-[124px] rounded-[13px] border-2 border-[#545351] flex flex-col justify-between overflow-hidden relative"
    :class="{'pointer-events-none': remainingPurchaseCount === 0}"
    :style="cardStyle"
  >
    <div
      class="absolute inset-0"
      style="background: linear-gradient(0deg, rgba(0, 0, 0, 0.44) 0%, rgba(0, 0, 0, 0.02) 79.81%, rgba(0, 0, 0, 0.00) 100%);"
    />
    <div
      class="z-[3] absolute top-1 left-1 rounded-full bg-tropee-black border border-[#545351] py-1 px-1.5 flex items-center justify-center font-pilat text-[10px] leading-[10px] font-black red-stroke-white-text red-stroke-black-text"
      :class="{'min-w-[72px]': countdownForLimitedPackEnd <= (3600 * 72)}"
    >
      {{ formatDuration(countdownForLimitedPackEnd, undefined, countdownForLimitedPackEnd > (3600 * 72)) }}
    </div>
    <div
      class="absolute inset-0 m-auto w-[60%] h-[64px] rounded-full opacity-[0.66] blur-[7.68px] mt-0"
      style="background: radial-gradient(50% 50% at 50% 50%, #FFF 33.65%, rgba(255, 255, 255, 0.00) 100%);"
    />
    <div
      class="flex-1 flex items-center justify-center gap-2"
      style="filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.44));"
    >
      <template
        v-for="(item, index) in benefitDisplayItems"
        :key="index"
      >
        <span
          v-if="index > 0"
          class="text-white text-[30px] leading-[30px] font-bold [text-shadow:0_0_8px_rgba(0,0,0,0.7),0_0_16px_rgba(0,0,0,0.5)]"
        >
          +
        </span>
        <div class="flex flex-col items-center">
          <div class="h-[35px] w-[35px] flex items-center justify-center z-[1]">
            <img
              :src="item.image"
              class="h-[35px] w-auto drop-shadow-[0_0_6px_rgba(0,0,0,0.44)] object-cover"
              :alt="item.alt"
            >
          </div>
          <span
            class="-mt-2.5 text-white text-[22px] leading-[26px] font-bold [text-shadow:0_0_8px_rgba(0,0,0,0.7),0_0_16px_rgba(0,0,0,0.5)] z-[2] black-0_66-stroke-white-text-3"
          >
            {{ item.value }}
          </span>
        </div>
      </template>
    </div>
    <div
      v-if="remainingPurchaseCount !== null && remainingPurchaseCount > 0"
      class="absolute left-0 right-0 bottom-[43px] w-full text-center text-shadow-2 text-white/[.87] text-xs font-semibold"
    >
      {{ remainingPurchaseCount }} / {{ limitedPack.config.maxPurchaseCount }} available
    </div>
    <div
      class="h-[42px] w-full bg-black/[.32] backdrop-blur-[3px] shadow-[inset_2px_4px_8px_0_rgba(255,255,255,0.12)] flex items-center justify-between pt-px"
    >
      <div
        class="pl-3 text-white uppercase [text-shadow:0_0_3.94px_rgba(0,0,0,0.55)] text-[24px] leading-7 font-bold -tracking-[2px]"
      >
        {{ limitedPack.name }}
      </div>
      <BaseButton
        style-type="primary-pink"
        class="min-w-[97px] !h-[29px] !max-h-[29px] !py-0 shadow-[inset_2px_4px_4px_0_rgba(255,255,255,0.24)] filter drop-shadow-[0_2px_6px_rgba(0,0,0,0.44)] !border-transparent !rounded-[8px] mr-[5px] !text-tropee-black !text-sm !font-bold"
        :disabled="remainingPurchaseCount === 0"
      >
        ${{ formatUnshortenedNumber(starPrice.usdAmount, true) }}
      </BaseButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.red-stroke-black-text {
	color: white;
	-webkit-text-stroke-width: 2px;
	-webkit-text-stroke-color: #E8405F;
	paint-order: stroke fill;
}

.black-0_66-stroke-white-text-3 {
	color: white;
	-webkit-text-stroke-width: 3px;
	-webkit-text-stroke-color: rgba(0, 0, 0, 0.66);
	paint-order: stroke fill;
}
</style>